/**
 * Spreadsheet helpers for the administration screens.
 *
 * The prototype pulled SheetJS from a CDN; here it is a real dependency, so the
 * bundle we ship is the bundle we tested and no third-party host sits inside the
 * page's script origin.
 */
/**
 * SheetJS is ~400kB and only three screens ever export. Loading it on demand
 * keeps it out of the bundle every user downloads on sign-in — which matters
 * because most of them are on a phone, and none of them are exporting.
 */
type Sheets = typeof import('xlsx');

let sheetsPromise: Promise<Sheets> | null = null;

function loadSheets(): Promise<Sheets> {
  sheetsPromise ??= import('xlsx');
  return sheetsPromise;
}

export type CellValue = string | number;

export interface SheetSpec {
  name: string;
  rows: CellValue[][];
}

/** Excel rejects a worksheet name longer than 31 characters. */
function safeSheetName(name: string): string {
  return name.slice(0, 31);
}

export async function downloadWorkbook(
  fileName: string,
  sheets: readonly SheetSpec[],
): Promise<void> {
  const XLSX = await loadSheets();
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sheet.rows), safeSheetName(sheet.name));
  }
  XLSX.writeFile(wb, fileName);
}

/** Read the first worksheet of an .xlsx/.csv upload as header-keyed rows. */
export async function readFirstSheet(file: File): Promise<Array<Record<string, unknown>>> {
  const XLSX = await loadSheets();
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const firstName = wb.SheetNames[0];
  const sheet = firstName === undefined ? undefined : wb.Sheets[firstName];
  if (!sheet) throw new Error('That file has no worksheets.');
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
}

export function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The Finance export — one row per receipt (Round 2, item 6).
 *
 * The claim's columns repeat on each of its receipt rows. That is deliberate
 * duplication: Finance filters and pivots this in Excel, and a sheet where the
 * claim id appears only on the first of eleven rows cannot be sorted without
 * destroying it.
 *
 * The server has already decided which rows this user may see; the browser only
 * formats them. A null amount is written as an empty cell rather than 0, so a
 * claim that has not reached a stage is not mistaken for one approved at zero.
 */
export interface PendingClaimExportRow {
  requestId: string;
  period: string;
  status: string;
  requesterName: string;
  employeeId: string;
  department: string;
  sapVendorCode: string | null;
  monthlyAllowance: number | null;
  /** Receipt-level. Null only for a claim with no rows, which should not exist. */
  lineNo: number | null;
  receiptDate: string | null;
  receiptNo: string | null;
  company: string | null;
  tin: string | null;
  description: string | null;
  receiptAmount: number | null;
  currency: string | null;
  originalAmount: number | null;
  fxRate: number | null;
  vatStatus: string | null;
  claimedAmount: number;
  financeReviewedAmount: number | null;
  releasedAmount: number | null;
  paymentReference: string;
  paymentStatus: string | null;
  releaseDate: number | null;
  financeReviewerName: string | null;
}

const PENDING_HEADERS: CellValue[] = [
  // Receipt first: the row's own identity comes before the claim it belongs to.
  'Receipt No.',
  'Receipt Date',
  'Company',
  'TIN',
  'Description',
  'Receipt Amount',
  'Currency',
  'Original Amount',
  'FX Rate',
  'VAT',
  // The claim, repeated on every one of its receipt rows.
  'Request ID',
  'Line',
  'Period',
  'Status',
  'Employee ID',
  'Requestor',
  'Department',
  'SAP Vendor Code',
  'Monthly Allowance',
  'Claimed Amount',
  'Finance Reviewed Amount',
  'Released Amount',
  'Payment Reference',
  'Payment Status',
  'Release Date',
  'Finance Reviewer',
];

/** Epoch milliseconds to a plain date cell; blank when there is no date. */
function dateCell(ts: number | null): CellValue {
  return ts == null ? '' : new Date(ts).toISOString().slice(0, 10);
}

export async function downloadPendingClaims(rows: readonly PendingClaimExportRow[]): Promise<void> {
  const body = rows.map((r): CellValue[] => [
    r.receiptNo ?? '',
    r.receiptDate ?? '',
    r.company ?? '',
    r.tin ?? '',
    r.description ?? '',
    r.receiptAmount ?? '',
    r.currency ?? '',
    // Only meaningful on a converted row; a peso receipt leaves both blank
    // rather than repeating its own figure as if a conversion had happened.
    r.originalAmount ?? '',
    r.fxRate ?? '',
    r.vatStatus ?? '',
    r.requestId,
    r.lineNo ?? '',
    r.period,
    r.status,
    r.employeeId,
    r.requesterName,
    r.department,
    r.sapVendorCode ?? '',
    r.monthlyAllowance ?? '',
    r.claimedAmount,
    r.financeReviewedAmount ?? '',
    r.releasedAmount ?? '',
    r.paymentReference,
    r.paymentStatus ?? '',
    dateCell(r.releaseDate),
    r.financeReviewerName ?? '',
  ]);

  await downloadWorkbook(`GDEC_Claim_Receipts_${todayStamp()}.xlsx`, [
    { name: 'Receipts', rows: [PENDING_HEADERS, ...body] },
  ]);
}
